import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { Search, FacetSearch } from 'models/index';
import { environment } from 'environments/environment';
import { Store } from '@ngrx/store';
import { AppState } from 'app/app.reducer';
import { SearchProps } from 'app/interfaces';
import { EXPORT_TIMEOUT_MS } from 'app/constants';

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private api: string;
  private httpOptions: any;
  private savedAuthors: any[] = [];
  private savedOutlets: any[] = [];

  constructor(
    private http: HttpClient,
    private store: Store<AppState>
  ) {
    this.api = `${environment.api_url}/search/content`;
    this.httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    this.store
      .select('authorsOutletsList')
      .subscribe(({ authors, outlets }) => {
        this.savedAuthors = authors || [];
        this.savedOutlets = outlets || [];
      });
  }

  public searchByTypeahead(keywords) {
    // TODO Don't call the API if there are no keywords
    // if (!keywords || keywords.length === 0) {
    //   return of([]);
    // }
    return this.http.get(
      `${environment.api_url}/search/${keywords}`,
      this.httpOptions
    );
  }

  public collectionContent(id: number, page: number = 0) {
    return this.http.post(
      `${environment.api_url}/content/collection/${id}/recent?page=${page}&size=50`,
      {},
      this.httpOptions
    );
  }

  public search(
    search: Search,
    sort: string,
    searchBy: string,
    p,
    s
  ): Observable<any> {
    let url = '';
    switch (searchBy) {
      case 'authors':
        url = `${this.api}/authors/${sort}?page=${p}&size=${s}`;
        break;
      case 'outlets':
        url = `${this.api}/outlets/${sort}?page=${p}&size=${s}`;
        break;
      case 'contents':
        url = `${this.api}/${sort}?page=${p}&size=${s}`;
        break;
    }
    return this.http.post(
      url,
      this.preProcessFilters(search),
      this.httpOptions
    );
  }

  public facetsSearch(
    search: Search,
    page: number = 0,
    size: number = 50
  ): Observable<any> {
    const url = `${this.api}/facets?page=${page}&size=${size}`;
    return this.http.post(
      url,
      this.preProcessFilters(search),
      this.httpOptions
    );
  }

  public searchContent(facetSearch: FacetSearch): Observable<any> {
    return this.http.post(
      `${this.api}/${facetSearch.sort}?page=${facetSearch.page}&size=${facetSearch.size}`,
      this.preProcessFilters(facetSearch.searchBody),
      this.httpOptions
    );
  }

  public coverageExportToExcel(filter: Search): Observable<Blob> {
    return this.http.post(`${this.api}/export`, this.preProcessFilters(filter), {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      responseType: 'blob' as 'blob',
    }).pipe(timeout(EXPORT_TIMEOUT_MS)) as Observable<Blob>;
  }

  public coverageExportToText(filter: Search): Observable<string> {
    const httpOptionsText = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      responseType: 'text' as 'text',
    };

    return this.http.post(
      `${this.api}/dump`,
      this.preProcessFilters(filter),
      httpOptionsText
    ).pipe(timeout(EXPORT_TIMEOUT_MS)) as Observable<string>;
  }

  public preProcessFilters<T extends SearchProps>(body: T): T {
    if (!body) { return body; }
    const requestBody = { ...body };

    if (body.authorFollowSelected === 'FOLLOWED_ONLY') {
      requestBody.authors = this.appendPartyIds(body.authors, this.savedAuthors);
    }
    if (body.authorFollowSelected === 'EXCLUDE_FOLLOWED') {
      requestBody.excludeAuthors = this.appendPartyIds(body.excludeAuthors, this.savedAuthors);
    }
    if (body.outletFollowSelected === 'FOLLOWED_ONLY') {
      requestBody.outlets = this.appendPartyIds(body.outlets, this.savedOutlets);
    }
    if (body.outletFollowSelected === 'EXCLUDE_FOLLOWED') {
      requestBody.excludeOutlets = this.appendPartyIds(body.excludeOutlets, this.savedOutlets);
    }

    delete requestBody.authorFollowSelected;
    delete requestBody.outletFollowSelected;
    return requestBody;
  }

  private appendPartyIds(currentIds: number[], items: any[]): number[] {
    return Array.from(
      new Set([
        ...(currentIds || []),
        ...(items || [])
          .map((item) => item.partyId)
          .filter((id) => id !== null && id !== undefined),
      ])
    );
  }
}
